// Copyright 2018 Sorama B.V.

import { Dimension } from "datatypes/SoramaWebApi";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";

export enum ImageStorageType {
    /** The image is stored locally. */
    Local = "local",
    /** The image is stored in the cloud. */
    Cloud = "cloud",
}

export interface ImageDescription {
    /** User provided name of the image. */
    name: string;

    /** Optional user provided description of the image. */
    description?: string;
}

export interface NewImage extends ImageDescription {
    /** Base 64 encoded image URI, should start with data:<mime-type>;base64 */
    data: string;
}

export interface Image extends ImageDescription {
    /** Id of the image. */
    id: string;

    /** Creation date of this image. */
    created: Date;

    /** Dimension of the two dimensional media object in pixels. */
    dimension: Dimension;

    /** Base64 encoded image containing a small preview of this image. */
    preview: string;

    /** Specifies where the image is stored. */
    type: ImageStorageType;
}

export interface AddedImage extends Image {
    /**
     * Indicates whether this image was actually added by the clients call to the server or if it is an existing image.
     */
    added?: boolean;
}

export abstract class IImageRetriever {
    /**
     * Get the image with the provided id
     * @param id Id of the image to get.
     */
    public abstract getImage(id: string): Promise<Image>;

    /**
     * Get base64 encoded image for the provided image id.
     * @param id Id of the image to get.
     */
    public abstract getImageUri(image: Image): Promise<string>;
}

export abstract class IImageService extends IImageRetriever {
    /**
     * Gets all images belonging to the businessunit the user has access to.
     */
    public abstract getImages(queryFilter?: string): IPagedRequest<Image>;

    /**
     * Upload an image to the server.
     * @param image The new image to upload.
     */
    public abstract createImage(image: NewImage): Promise<AddedImage>;

    /**
     * Delete an image on the server
     * @param id Id of the image to be deleted
     */
    public abstract deleteImage(id: string): Promise<void>;
}
